import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(null);
  const display = hover !== null ? hover : value;

  const resolve = (e, star) => {
    const r = e.currentTarget.getBoundingClientRect();
    const quarter = Math.max(0.25, Math.ceil(((e.clientX - r.left) / r.width) * 4) / 4);
    return star - 1 + quarter;
  };

  return (
    <Box sx={{ display: 'flex', gap: '6px', my: 1 }} onMouseLeave={() => setHover(null)}>
      {[1, 2, 3, 4, 5].map((star) => {
        const pct = Math.min(1, Math.max(0, display - (star - 1)));
        return (
          <Box
            key={star}
            onMouseMove={(e) => setHover(resolve(e, star))}
            onClick={(e) => onChange(resolve(e, star))}
            sx={{ position: 'relative', fontSize: '2rem', cursor: 'pointer', userSelect: 'none', lineHeight: 1 }}
          >
            <Box component="span" sx={{ color: '#CBD5E1' }}>★</Box>
            <Box
              component="span"
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                overflow: 'hidden',
                width: `${pct * 100}%`,
                color: '#1A7FFF',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              ★
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function ReviewFormPage() {
  const [anonimo, setAnonimo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [plan, setPlan] = useState('');
  const [tiempo, setTiempo] = useState('');
  const [rating, setRating] = useState(0);
  const [antes, setAntes] = useState('');
  const [ahora, setAhora] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!plan) {
      setError('Selecciona tu coach antes de enviar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: anonimo ? 'Anónimo' : nombre, anonimo, plan, tiempo, rating, texto: `Antes: ${antes.trim()} Ahora: ${ahora.trim()}` }),
      });
      if (res.status === 201) {
        setSuccess(true);
        setAnonimo(false);
        setNombre('');
        setPlan('');
        setTiempo('');
        setRating(0);
        setAntes('');
        setAhora('');
      } else {
        const data = await res.json();
        setError(data.error || 'Hubo un error al enviar. Inténtalo de nuevo o escríbenos por WhatsApp.');
      }
    } catch {
      setError('Hubo un error al enviar. Inténtalo de nuevo o escríbenos por WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src="/logo.jpg" alt="AU Coaching Services" style={{ maxWidth: 160, maxHeight: 80 }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontFamily: 'Kanit, sans-serif' }}>
            Deja tu reseña
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', mb: 3, fontFamily: 'Kanit, sans-serif' }}>
            Tu experiencia ayuda a otros a tomar la decisión. Sé honesto/a — eso vale más que una opinión perfecta.
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ¡Gracias! Tu reseña fue recibida y será publicada pronto.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <FormControlLabel
              control={
                <Checkbox
                  checked={anonimo}
                  onChange={(e) => setAnonimo(e.target.checked)}
                  sx={{ color: '#1A7FFF', '&.Mui-checked': { color: '#1A7FFF' } }}
                />
              }
              label={<Typography sx={{ fontFamily: 'Kanit, sans-serif', fontSize: '0.9rem', color: '#64748B' }}>Quiero aparecer de forma anónima</Typography>}
              sx={{ mb: 0.5 }}
            />

            {!anonimo && (
              <TextField
                label="Nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                fullWidth
                inputProps={{ maxLength: 80 }}
                margin="normal"
              />
            )}

            <FormControl fullWidth margin="normal">
              <InputLabel>Coach</InputLabel>
              <Select
                value={plan}
                label="Coach"
                onChange={(e) => setPlan(e.target.value)}
              >
                <MenuItem value="Head Coach">Head Coach – Agustín Urzua</MenuItem>
                <MenuItem value="Team Coach">Team Coach AU</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Semanas en AU Coaching"
              type="number"
              value={tiempo}
              onChange={(e) => {
                const v = Math.min(250, Math.max(1, parseInt(e.target.value) || ''));
                setTiempo(v === '' ? '' : String(v));
              }}
              fullWidth
              inputProps={{ min: 1, max: 250 }}
              helperText={tiempo ? `${tiempo} semana${tiempo === '1' ? '' : 's'}` : '¿Cuántas semanas llevas entrenando con nosotros?'}
              margin="normal"
            />

            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: rating === 0 ? '#EF4444' : '#64748B', fontFamily: 'Kanit, sans-serif' }}
              >
                Calificación{rating === 0 && ' (requerida)'}
              </Typography>
              <StarRating value={rating} onChange={setRating} />
            </Box>

            <TextField
              label="Antes de AU Coaching"
              placeholder="¿Cómo era tu situación? Físico, mentalidad, motivación, estancamiento..."
              InputLabelProps={{ shrink: true }}
              value={antes}
              onChange={(e) => setAntes(e.target.value)}
              required
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              helperText={`${antes.length}/500`}
              margin="normal"
            />

            <TextField
              label="Aprendizajes y qué cambió"
              placeholder="¿Qué aprendiste? ¿Qué cambió en tu físico, rendimiento, mentalidad o confianza?"
              InputLabelProps={{ shrink: true }}
              value={ahora}
              onChange={(e) => setAhora(e.target.value)}
              required
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              helperText={`${ahora.length}/500`}
              margin="normal"
              sx={{ mb: 1 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 2,
                py: 1.5,
                bgcolor: '#1A7FFF',
                '&:hover': { bgcolor: '#1568D9' },
                fontFamily: 'Kanit, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
              }}
            >
              {loading ? 'Enviando...' : 'Enviar reseña'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
